import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../components/AppNav';
import CompleteProfileNudge from '../components/CompleteProfileNudge';
import { apiClient } from '../api';
import { useAuth } from '../context/AuthContext';
import { Eye, MapPin, User } from 'lucide-react';

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const Views = () => {
  const { user, wsSubscribe } = useAuth();
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/views/me');
      setViewers(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Realtime: prepend new viewers via the shared WebSocket
  useEffect(() => {
    if (!user) return;
    return wsSubscribe((data) => {
      if (data?.type === 'profile_view' && data.viewer) {
        setViewers((prev) => {
          const filtered = prev.filter((p) => p.user_id !== data.viewer.user_id);
          return [{ ...data.viewer, last_viewed_at: new Date().toISOString(), view_count: 1 }, ...filtered];
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, wsSubscribe]);

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Eye className="w-7 h-7 text-fnl-pink" />
          <h1 className="text-3xl font-extrabold text-white">Who viewed you</h1>
        </div>
        <CompleteProfileNudge context="views" />

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin" />
          </div>
        ) : viewers.length === 0 ? (
          <div className="bg-fnl-purple/60 rounded-2xl p-10 border border-purple-900/40 text-center" data-testid="views-empty">
            <Eye className="w-12 h-12 text-fnl-pink/40 mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">No views yet</p>
            <p className="text-purple-300 text-sm">When someone opens your profile, they'll show up here in real-time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="views-grid">
            {viewers.map((v) => (
              <Link
                to={`/profile/${v.user_id}`}
                key={v.user_id}
                className="bg-fnl-purple/60 rounded-2xl border border-purple-900/40 overflow-hidden group hover:border-fnl-pink/60 transition-colors"
                data-testid={`viewer-${v.user_id}`}
              >
                <div className="aspect-[4/5] bg-fnl-purple-light/60 relative">
                  {v.photo_base64 ? (
                    <img src={v.photo_base64} alt={v.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-fnl-pink/40" />
                    </div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-3">
                    <div className="text-white font-bold flex items-center gap-2">
                      {v.name}, <span className="text-fnl-pink">{v.age}</span>
                    </div>
                    {v.location && (
                      <div className="text-purple-200 text-xs flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />{v.location}
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-3 py-2 flex items-center justify-between text-[11px]">
                  <span className="text-purple-300">{timeAgo(v.last_viewed_at)}</span>
                  {v.view_count > 1 && (
                    <span className="text-fnl-pink font-semibold">{v.view_count}× views</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Views;
